import { supabase } from '../lib/supabaseClient';
import { ApiErrorConCodigo, esErrorPermanente } from '../lib/apiErrors';
import { setCanjeUploader } from '../offline/canjeQueue';
import type { DinamicaConfig, ReglaPremioTotal } from '../domain/canjes';
import { completarTrabajadores } from './trabajadoresDeMisTiendas';

const BUCKET = 'photos';

/** Error del registro de canjes con su código, para que la cola distinga red de rechazo. */
export class CanjeApiError extends ApiErrorConCodigo {
  constructor(message: string, code: string | null = null) {
    super(message, code, 'CanjeApiError');
  }
}

/** Igual que en `api/positions.ts`: PostgREST trunca en silencio a 1000 filas. */
const CANJE_PAGE_SIZE = 1000;

/**
 * Hint de FK OBLIGATORIO en el embed a `profiles`: `canje_records` tiene DOS claves foráneas
 * a esa tabla (`worker_id` y `created_by`), y sin el hint PostgREST no sabe cuál seguir. Es la
 * misma trampa que `assignments`.
 *
 * `worker_id` viaja además del embed porque el supervisor de ruta no lee `profiles` (0218): para él
 * el embed llega en null y `completarTrabajadores` lo rellena buscando por esa columna.
 */
const CANJE_SELECT =
  `id, campaign_id, worker_id, assignment_id, fecha, registrado_en, ocurrido_en, cliente_nombre, cliente_contacto, monto_compra,
   observaciones, marcas_dejadas, estado,
   fuera_de_programacion, registro_tardio, sin_asistencia,
   foto_boleta_path, boleta_numero, foto_premio_path, origen, motivo_carga,
   pdv:pdv_id(id, code, name),
   worker:profiles!canje_records_worker_id_fkey(id, dni, first_names, last_names),
   creador:profiles!canje_records_created_by_fkey(first_names, last_names),
   canje_lines(dynamic_id, canjes,
     dynamic:campaign_dynamics(code, name, trigger_kind),
     canje_line_prizes(units, equivalencia, prize:campaign_prizes(name)))`;

// ---------------------------------------------------------------------------
// Configuración de la campaña
// ---------------------------------------------------------------------------

export interface ConfigCanjes {
  /** Tope de canjes por cliente. `null` = la campaña no tiene tope. */
  maxPerClient: number | null;
  /** Meta de canjes por jornada. Solo informa; superarla es bueno. */
  dailyGoal: number | null;
  dinamicas: DinamicaConfig[];
  premios: { id: string; name: string; requiresContact: boolean }[];
  /** Marcas de la competencia para "¿qué marca dejó de comprar?" (0031). */
  competitorBrands: string[];
  /**
   * Si se muestra la pregunta de FOTO de la boleta (0043). Apagarla la oculta; las fotos ya
   * subidas se conservan. Cuando se muestra sigue siendo opcional (0036).
   */
  pideFotoBoleta: boolean;
  /** Si se muestra el campo alfanumérico del NÚMERO de boleta (0043). Independiente de la foto. */
  pideNumeroBoleta: boolean;
  /**
   * Premio por el TOTAL del canje (0386): «cada 3 tiras = 1 Licua-Takis, máximo 3». `null` = el premio sale de cada
   * dinámica, como siempre. Con regla, los formularios no preguntan premio por línea: lo calculan.
   */
  premioPorTotal: ReglaPremioTotal | null;
  /** Cómo se llama lo que se cuenta en cada línea («tiras»). `null` = canjes. */
  unidadPlural: string | null;
}

/**
 * Configuración de canjes para UN turno concreto: campaña, tienda y fecha.
 *
 * Recibe el PDV y la fecha —y no solo la campaña— porque desde la 0029 una dinámica puede valer
 * solo en ciertas tiendas y ciertos días. Ofrecerle a la impulsadora de Makro Piura, un martes,
 * una promoción que allí no existe no es un detalle cosmético: los canjes que registre quedan en
 * la campaña como si la promoción hubiera corrido ahí.
 */
export async function fetchConfigCanjes(
  campaignId: string,
  pdvId: string,
  fecha: string,
): Promise<ConfigCanjes> {
  const [ajustes, dinamicas, premios, vigentes] = await Promise.all([
    supabase
      .from('campaign_dynamic_settings')
      .select(`max_per_client, daily_goal, competitor_brands, pide_foto_boleta, pide_numero_boleta,
               premio_total_prize_id, premio_total_cada, premio_total_tope, unidad_plural`)
      .eq('campaign_id', campaignId)
      .maybeSingle(),
    supabase
      .from('campaign_dynamics')
      .select(`id, code, name, trigger_kind, min_amount, trigger_note, sort,
               prize_selection, requires_client_name,
               dynamic_expected_prizes(prize_id, units)`)
      .eq('campaign_id', campaignId)
      .eq('active', true)
      .order('sort'),
    supabase
      .from('campaign_prizes')
      .select('id, name, requires_contact')
      .eq('campaign_id', campaignId)
      .eq('active', true)
      .order('name'),
    supabase.rpc('dinamicas_vigentes', {
      p_campaign: campaignId, p_pdv: pdvId, p_fecha: fecha,
    }),
  ]);

  if (ajustes.error) throw new Error(ajustes.error.message);
  if (dinamicas.error) throw new Error(dinamicas.error.message);
  if (premios.error) throw new Error(premios.error.message);

  type RawDinamica = {
    id: string; code: string; name: string;
    trigger_kind: 'monto' | 'producto';
    min_amount: string | number | null;
    trigger_note: string | null;
    prize_selection: 'esperado' | 'elegido' | null;
    requires_client_name: boolean | null;
    dynamic_expected_prizes: { prize_id: string; units: number }[] | null;
  };

  // Si la RPC de vigencia falla (0029 sin aplicar, o red), se ofrecen TODAS las dinámicas
  // activas: es el comportamiento anterior. Es la elección menos mala de las dos: dejar a la
  // mercadista sin ninguna dinámica que registrar la deja parada frente al cliente, mientras
  // que ofrecer de más produce un canje que el servidor marca con `fuera_de_dinamica` (0030) y
  // que el coordinador ve y corrige. Ninguna lectura que pueda fallar debe poder vaciarle la
  // pantalla a quien está trabajando.
  const idsVigentes = vigentes.error
    ? null
    : new Set(((vigentes.data ?? []) as { id: string }[]).map((d) => d.id));

  return {
    maxPerClient: ajustes.data?.max_per_client ?? null,
    dailyGoal: ajustes.data?.daily_goal ?? null,
    // `min_amount` es numeric: PostgREST lo entrega como cadena.
    dinamicas: ((dinamicas.data ?? []) as unknown as RawDinamica[])
      .filter((d) => idsVigentes === null || idsVigentes.has(d.id))
      .map((d) => ({
        id: d.id,
        code: d.code,
        name: d.name,
        triggerKind: d.trigger_kind,
        minAmount: d.min_amount === null ? null : Number(d.min_amount),
        esperados: (d.dynamic_expected_prizes ?? []).map((e) => ({
          prizeId: e.prize_id,
          units: e.units,
        })),
        // Los respaldos NO son cosmética: si la 0030 no estuviera aplicada, PostgREST no
        // devuelve estas columnas y quedarían `undefined`. Cayendo al comportamiento anterior
        // ('esperado', nombre obligatorio) la pantalla sigue funcionando exactamente como antes
        // en vez de quedarse sin poder decidir qué preguntar.
        prizeSelection: d.prize_selection ?? 'esperado',
        requiresClientName: d.requires_client_name ?? true,
      })),
    premios: ((premios.data ?? []) as unknown as
      { id: string; name: string; requires_contact?: boolean }[])
      .map((p) => ({ id: p.id, name: p.name, requiresContact: p.requires_contact ?? false })),
    // Respaldo vacío si la 0031 no está aplicada: la pantalla cae a su lista de siempre.
    competitorBrands:
      (ajustes.data as { competitor_brands?: string[] } | null)?.competitor_brands ?? [],
    // Respaldos con el comportamiento anterior a la 0043 —foto sí, número no—, por la misma razón
    // que los de arriba: si la migración no estuviera aplicada, PostgREST no devuelve estas
    // columnas y `undefined` dejaría a la impulsadora sin ninguna forma de anotar la boleta.
    pideFotoBoleta:
      (ajustes.data as { pide_foto_boleta?: boolean } | null)?.pide_foto_boleta ?? true,
    pideNumeroBoleta:
      (ajustes.data as { pide_numero_boleta?: boolean } | null)?.pide_numero_boleta ?? false,
    premioPorTotal: reglaDeAjustes(ajustes.data as AjustesPremioTotal | null),
    unidadPlural: (ajustes.data as AjustesPremioTotal | null)?.unidad_plural ?? null,
  };
}

type AjustesPremioTotal = {
  premio_total_prize_id?: string | null;
  premio_total_cada?: number | null;
  premio_total_tope?: number | null;
  unidad_plural?: string | null;
};

/** La regla solo vale completa: premio y «cada». El CHECK de la 0386 no deja guardar una a medias. */
function reglaDeAjustes(a: AjustesPremioTotal | null): ReglaPremioTotal | null {
  if (!a?.premio_total_prize_id || !a.premio_total_cada) return null;
  return { prizeId: a.premio_total_prize_id, cada: a.premio_total_cada, tope: a.premio_total_tope ?? null };
}

// ---------------------------------------------------------------------------
// Registrar
// ---------------------------------------------------------------------------

/**
 * Línea tal como se guarda. NO es `LineaCanje` del dominio: aquí cada premio lleva además su
 * bandera `equivalencia`, que es un dato persistido (lo entregado difirió de lo esperado por
 * falta de stock). El dominio la CALCULA con `esEquivalencia`; esta capa la GUARDA. Mezclar los
 * dos tipos obligaría a meter un campo de persistencia en el módulo puro.
 *
 * Es estructuralmente compatible con `LineaCanje`, así que las reglas del dominio siguen
 * aceptándola sin conversión.
 */
export interface LineaCanjeParaGuardar {
  dynamicId: string;
  canjes: number;
  premios: { prizeId: string; units: number; equivalencia?: boolean }[];
}

export interface RegistrarCanjeInput {
  clientUuid: string;
  campaignId: string;
  pdvId: string;
  /** `null` cuando no había programación: el servidor lo marca como fuera de programación. */
  assignmentId: string | null;
  fecha: string;
  ocurridoEn: string | null;
  /** `null` cuando la dinámica no pide nombre (0030). */
  clienteNombre: string | null;
  clienteContacto: string | null;
  montoCompra: number | null;
  marcasDejadas: string[];
  observaciones: string | null;
  /** `null` si la tienda no permitió tomar la foto (0036). */
  fotoBoletaPath: string | null;
  /** Número de boleta tipeado (0043). `null` cuando la campaña no lo pide o quedó vacío. */
  boletaNumero: string | null;
  fotoPremioPath: string | null;
  lineas: LineaCanjeParaGuardar[];
}

/**
 * Sube una foto de evidencia del canje. Va al prefijo `form-photos/`, que ya tiene políticas de
 * INSERT y de UPDATE en la 0004 — por eso este módulo no necesita migración de Storage.
 *
 * `upsert: true` es el mecanismo de idempotencia del reintento de la cola: mismo path, mismo
 * contenido. Sin el UPDATE en la política, el primer reintento moriría con 42501 y el canje
 * quedaría atascado en el celular para siempre.
 */
export async function uploadCanjePhoto(
  clientUuid: string, tipo: 'boleta' | 'premio', photo: Blob,
): Promise<string> {
  const path = `form-photos/canje-${clientUuid}-${tipo}.jpg`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, photo, {
    contentType: 'image/jpeg',
    upsert: true,
  });
  if (error) {
    const code = (error as { statusCode?: string | number }).statusCode;
    throw new CanjeApiError(error.message, code === undefined ? null : String(code));
  }
  return path;
}

/**
 * Sube la foto que el coordinador eligió para CORREGIR un canje ya registrado, y devuelve su ruta.
 *
 * **Por qué un uuid nuevo en cada llamada y no el `client_uuid` del registro.** `uploadCanjePhoto`
 * sube con `upsert: true`, que es lo correcto para el celular —reintentar la misma foto no debe
 * fallar— pero acá sería destructivo: reusar el uuid del registro pisaría el archivo que subió el
 * campo, y la evidencia original desaparecería del bucket aunque el historial siguiera guardando
 * su ruta. Con una ruta nueva por corrección no se sobrescribe nada nunca: la foto vieja queda
 * donde estaba y el rastro de `canje_corrections` la sigue apuntando, así que se puede volver a
 * mirar lo que el campo reportó de verdad.
 *
 * La foto vieja tampoco se borra a propósito. Supabase no permite borrar del Storage por SQL, y el
 * proyecto ya tiene la lección del borrado que dejó fotos huérfanas; además borrarla convertiría el
 * historial en un puntero a nada.
 *
 * La ruta se mantiene bajo `form-photos/`: es el único prefijo que `corregir_canje` acepta desde la
 * 0057, justamente para que nadie pueda apuntar el campo a `documents/` (fotos de DNI).
 */
export function subirFotoDeCorreccion(tipo: 'boleta' | 'premio', foto: Blob): Promise<string> {
  return uploadCanjePhoto(crypto.randomUUID(), tipo, foto);
}

/**
 * Registra el canje completo con UNA llamada a `register_canje`: cabecera, líneas y premios
 * entran en la misma transacción o no entra nada.
 *
 * `worker_id` NO viaja en el payload: lo pone el servidor desde `auth.uid()`. Ahí se cierra el
 * agujero de DataScope, donde la impulsadora escribía su propio nombre a mano y "Tatiana
 * anchante Castro" y "Tatiana anchante castro" eran dos personas distintas.
 *
 * La RPC es idempotente por `client_uuid`: reintentar devuelve el mismo id.
 */
export async function registrarCanje(input: RegistrarCanjeInput): Promise<string> {
  const { data, error } = await supabase.rpc('register_canje', {
    p_payload: {
      client_uuid: input.clientUuid,
      campaign_id: input.campaignId,
      pdv_id: input.pdvId,
      assignment_id: input.assignmentId,
      fecha: input.fecha,
      ocurrido_en: input.ocurridoEn,
      cliente_nombre: input.clienteNombre,
      cliente_contacto: input.clienteContacto,
      monto_compra: input.montoCompra,
      marcas_dejadas: input.marcasDejadas,
      observaciones: input.observaciones,
      foto_boleta_path: input.fotoBoletaPath,
      boleta_numero: input.boletaNumero,
      foto_premio_path: input.fotoPremioPath,
      lineas: input.lineas.map((l) => ({
        dynamic_id: l.dynamicId,
        canjes: l.canjes,
        premios: l.premios.map((p) => ({
          prize_id: p.prizeId,
          units: p.units,
          equivalencia: p.equivalencia ?? false,
        })),
      })),
    },
  });
  if (error) throw new CanjeApiError(error.message, error.code ?? null);
  return data as string;
}

// ---------------------------------------------------------------------------
// Cargar a mano desde la bandeja (0374)
// ---------------------------------------------------------------------------

/** Persona a la que se le puede cargar un canje: tiene al menos un turno aprobado en la campaña. */
export interface RecursoParaCarga {
  workerId: string;
  nombre: string;
  dni: string;
  /** Tenía turno aprobado en esa tienda ese día. Si no, el canje queda fuera de programación. */
  programada: boolean;
}

/**
 * La lista sale de una RPC y no de leer `positions`: una campaña larga pasa las 1 000 jornadas y
 * PostgREST corta ahí sin avisar. La RPC además rechaza a quien no puede cargar en la campaña.
 */
export async function recursosParaCargarCanje(
  campaignId: string, pdvId: string, fecha: string,
): Promise<RecursoParaCarga[]> {
  const { data, error } = await supabase.rpc('recursos_para_cargar_canje', {
    p_campaign: campaignId, p_pdv: pdvId, p_fecha: fecha,
  });
  if (error) throw new Error(error.message);
  return ((data ?? []) as { worker_id: string; nombre: string; dni: string; programada: boolean }[])
    .map((r) => ({ workerId: r.worker_id, nombre: r.nombre, dni: r.dni, programada: r.programada }));
}

/**
 * Lo mismo que registra el celular, MENOS `assignmentId` (lo resuelve el servidor: el turno
 * aprobado de esa persona en esa tienda ese día) y MÁS `workerId`, que acá es otra persona.
 */
export type CargarCanjeInput = Omit<RegistrarCanjeInput, 'assignmentId'> & { workerId: string };

/**
 * Carga un canje a nombre de `workerId`, con motivo obligatorio. Queda con `origen = 'bandeja'` y
 * el autor en `created_by`.
 *
 * Idempotente por `clientUuid`, igual que `registrarCanje`: la pantalla acuña uno por canje y lo
 * reusa en los reintentos, así un corte de red a mitad de la respuesta no duplica el canje.
 */
export async function registrarCanjeManual(input: CargarCanjeInput, motivo: string): Promise<string> {
  const { data, error } = await supabase.rpc('registrar_canje_manual', {
    p_payload: {
      client_uuid: input.clientUuid,
      campaign_id: input.campaignId,
      pdv_id: input.pdvId,
      worker_id: input.workerId,
      fecha: input.fecha,
      ocurrido_en: input.ocurridoEn,
      cliente_nombre: input.clienteNombre,
      cliente_contacto: input.clienteContacto,
      monto_compra: input.montoCompra,
      marcas_dejadas: input.marcasDejadas,
      observaciones: input.observaciones,
      foto_boleta_path: input.fotoBoletaPath,
      boleta_numero: input.boletaNumero,
      foto_premio_path: input.fotoPremioPath,
      lineas: input.lineas.map((l) => ({
        dynamic_id: l.dynamicId,
        canjes: l.canjes,
        premios: l.premios.map((p) => ({
          prize_id: p.prizeId, units: p.units, equivalencia: p.equivalencia ?? false,
        })),
      })),
    },
    p_motivo: motivo,
  });
  if (error) throw new CanjeApiError(error.message, error.code ?? null);
  return data as string;
}

// ---------------------------------------------------------------------------
// Leer
// ---------------------------------------------------------------------------

export interface CanjePremioRow {
  prizeName: string;
  units: number;
  equivalencia: boolean;
}

export interface CanjeLineaRow {
  dynamicId: string;
  dynamicCode: string;
  dynamicName: string;
  canjes: number;
  /**
   * Qué dispara el canje en esta dinámica: `'monto'` pide el monto de la boleta, `'producto'` no.
   *
   * Viaja hasta acá porque es la única forma de distinguir «la campaña no pide monto» de «pide
   * monto y nadie lo anotó». El reporte necesita esa diferencia: en el primer caso oculta las
   * cifras de monto; en el segundo tiene que mostrar el hueco, porque es un dato que falta.
   * Antes había que adivinarlo mirando si algún registro traía monto, y una campaña de monto sin
   * ningún monto anotado se veía idéntica a una de producto.
   *
   * `null` cuando RLS anula el embed de la dinámica (BITACORA §3.7): es DESCONOCIDO, no
   * `'producto'`. Quien lo lea tiene que tratar los tres casos.
   */
  triggerKind: 'monto' | 'producto' | null;
  premios: CanjePremioRow[];
}

export interface CanjeRow {
  id: string;
  /** Campaña dueña del registro. La bandeja del panel filtra y agrupa por ella. */
  campaignId: string;
  /**
   * El turno al que se amarró el canje. `null` = fuera de programación. Es lo que usa la vista «Por turno» para ponerlo
   * dentro de su jornada (0375).
   */
  assignmentId: string | null;
  fecha: string;
  /** Hora del servidor. No la modifica nadie. */
  registradoEn: string;
  /** Momento declarado, si se declaró otro distinto del registro. */
  ocurridoEn: string | null;
  /**
   * `null` cuando la dinámica no pide nombre (0030: *"no todas las dinámicas piden el nombre del
   * cliente; en un raspa y gana la evidencia es la boleta"*). Decía `string` y mentía: TypeScript
   * dejaba pasar `r.clienteNombre.trim()` y la descarga del HTML de canjes reventaba —en inglés y
   * después de haber bajado decenas de MB de fotos— en toda campaña con `requires_client_name`
   * apagado. Todo lector lo trata como opcional: `?? ''` para contar, `|| '—'` para mostrar.
   */
  clienteNombre: string | null;
  clienteContacto: string | null;
  montoCompra: number | null;
  observaciones: string | null;
  marcasDejadas: string[];
  estado: 'registrado' | 'anulado';
  fueraDeProgramacion: boolean;
  registroTardio: boolean;
  sinAsistencia: boolean;
  /** `null` si se registró sin foto de boleta (0036). */
  fotoBoletaPath: string | null;
  /** Número de boleta tipeado (0043). Convive con la foto: son dos campos independientes. */
  boletaNumero: string | null;
  fotoPremioPath: string | null;
  /**
   * `'campo'` = lo registró la persona desde el celular. `'bandeja'` = lo cargó a mano alguien de
   * oficina desde Registros de campo (0374). Se muestra en la bandeja y en el Excel, que son
   * internos; el HTML que va al cliente no lo dice.
   */
  origen: 'campo' | 'bandeja';
  /** Por qué se cargó a mano. `null` en lo registrado desde el campo. */
  motivoCarga: string | null;
  /** Quién lo cargó a mano. `null` en lo del campo, y para quien no lee perfiles (BITACORA §3.7). */
  cargadoPor: { firstNames: string; lastNames: string } | null;
  pdv: { id: string; code: string; name: string } | null;
  /** Bajo RLS un embed sin permiso devuelve `null`: la fila existe igual (BITACORA §3.7). */
  worker: { id: string; dni: string; firstNames: string; lastNames: string } | null;
  lineas: CanjeLineaRow[];
  /** Suma de los canjes de las líneas. Es la unidad con la que cuenta el cliente. */
  totalCanjes: number;
}

type RawCanje = {
  id: string; campaign_id: string; worker_id: string | null; assignment_id: string | null;
  fecha: string; registrado_en: string; ocurrido_en: string | null;
  // Nulable desde la 0030, igual que `CanjeRow.clienteNombre`. Si este tipo miente, el de arriba
  // hereda la mentira: `aCanjeRow` pasa el valor crudo tal cual.
  cliente_nombre: string | null; cliente_contacto: string | null;
  monto_compra: string | number | null; observaciones: string | null;
  marcas_dejadas: string[] | null; estado: string;
  fuera_de_programacion: boolean; registro_tardio: boolean; sin_asistencia: boolean;
  foto_boleta_path: string | null; boleta_numero: string | null;
  foto_premio_path: string | null;
  // Nacen en la 0374: en una base sin ella PostgREST rechaza la consulta entera, así que no hay
  // estado intermedio que tolerar acá.
  origen: string; motivo_carga: string | null;
  pdv: { id: string; code: string; name: string } | null;
  worker: { id: string; dni: string; first_names: string; last_names: string } | null;
  creador: { first_names: string; last_names: string } | null;
  canje_lines: {
    dynamic_id: string; canjes: number;
    dynamic: { code: string; name: string; trigger_kind: string | null } | null;
    canje_line_prizes: {
      units: number; equivalencia: boolean; prize: { name: string } | null;
    }[] | null;
  }[] | null;
};

function aCanjeRow(r: RawCanje): CanjeRow {
  const lineas: CanjeLineaRow[] = (r.canje_lines ?? []).map((l) => ({
    dynamicId: l.dynamic_id,
    dynamicCode: l.dynamic?.code ?? '',
    dynamicName: l.dynamic?.name ?? '',
    canjes: l.canjes,
    triggerKind: l.dynamic?.trigger_kind === 'monto'
      ? 'monto'
      : l.dynamic?.trigger_kind === 'producto' ? 'producto' : null,
    premios: (l.canje_line_prizes ?? []).map((p) => ({
      prizeName: p.prize?.name ?? '',
      units: p.units,
      equivalencia: p.equivalencia,
    })),
  }));
  return {
    id: r.id,
    campaignId: r.campaign_id,
    assignmentId: r.assignment_id,
    fecha: r.fecha,
    registradoEn: r.registrado_en,
    ocurridoEn: r.ocurrido_en,
    clienteNombre: r.cliente_nombre,
    clienteContacto: r.cliente_contacto,
    // numeric llega como cadena. Nulo se conserva nulo: "no se anotó" no es cero, y en una
    // dinámica de producto no hay monto que anotar.
    montoCompra: r.monto_compra === null ? null : Number(r.monto_compra),
    observaciones: r.observaciones,
    marcasDejadas: r.marcas_dejadas ?? [],
    estado: r.estado as 'registrado' | 'anulado',
    fueraDeProgramacion: r.fuera_de_programacion,
    registroTardio: r.registro_tardio,
    sinAsistencia: r.sin_asistencia,
    fotoBoletaPath: r.foto_boleta_path,
    // `?? null` y no `?? ''`: la columna nace en la 0043, y en una base sin ella PostgREST la
    // omite. Vacío y "no existe la columna" tienen que verse igual, o la bandeja mostraría
    // un número de boleta en blanco como si alguien lo hubiera dejado sin llenar.
    boletaNumero: r.boleta_numero ?? null,
    fotoPremioPath: r.foto_premio_path,
    origen: r.origen === 'bandeja' ? 'bandeja' : 'campo',
    motivoCarga: r.motivo_carga,
    // Solo en lo cargado a mano: en lo del campo el creador es la misma persona, y repetirla acá
    // haría que «cargado por» apareciera en todas las filas.
    cargadoPor: r.origen === 'bandeja' && r.creador
      ? { firstNames: r.creador.first_names, lastNames: r.creador.last_names }
      : null,
    pdv: r.pdv,
    worker: r.worker
      ? {
          id: r.worker.id,
          dni: r.worker.dni,
          firstNames: r.worker.first_names,
          lastNames: r.worker.last_names,
        }
      : null,
    lineas,
    totalCanjes: lineas.reduce((s, l) => s + l.canjes, 0),
  };
}

/** Aplica el filtro, pagina, y LANZA si lo recibido no cuadra con el total. */
async function leerCanjes(
  aplicarFiltros: (q: ReturnType<typeof construirConsulta>) => ReturnType<typeof construirConsulta>,
): Promise<CanjeRow[]> {
  const raw: RawCanje[] = [];
  let total: number | null = null;
  for (let from = 0; ; from += CANJE_PAGE_SIZE) {
    const { data, error, count } = await aplicarFiltros(construirConsulta())
      .order('fecha', { ascending: false })
      .order('registrado_en', { ascending: false })
      .order('id')
      .range(from, from + CANJE_PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    if (count !== null && count !== undefined) total = count;
    const batch = (data ?? []) as unknown as RawCanje[];
    raw.push(...batch);
    if (batch.length < CANJE_PAGE_SIZE) break;
  }
  if (total !== null && raw.length !== total) {
    throw new Error(
      `Lectura incompleta de canjes: se recibieron ${raw.length} de ${total} filas. ` +
      'Acota el rango de fechas y vuelve a intentar.',
    );
  }
  // Una sola vez para todas las páginas, y antes de transformar: muta las filas crudas.
  await completarTrabajadores(raw);
  return raw.map(aCanjeRow);
}

function construirConsulta() {
  return supabase.from('canje_records').select(CANJE_SELECT, { count: 'exact' });
}

/** Una jornada programada tal como la devuelve `jornadas_para_canjes` (0375). */
export interface JornadaCanje {
  positionId: string;
  campaignId: string;
  fecha: string;
  /** `HH:mm`. */
  inicio: string;
  fin: string;
  slot: number;
  pdv: { id: string; code: string; name: string };
  /** La persona APROBADA en la jornada. `null` = nadie asignado (o solo rechazadas o reemplazadas). */
  asignacion: { id: string; workerId: string; nombre: string; dni: string } | null;
  /** Primera marca de ingreso (ISO, hora del celular). `null` = no marcó. */
  ingreso: string | null;
}

/** Filtros de la bandeja. Todo opcional salvo el rango: sin rango, la lectura no se acota. */
export interface FiltrosBandeja {
  desde: string;
  hasta: string;
  campaignId?: string | null;
  pdvId?: string | null;
  workerId?: string | null;
  dynamicId?: string | null;
  /** `null` = ambos estados. Por defecto la bandeja muestra los registrados. */
  estado?: 'registrado' | 'anulado' | null;
  /** Cada bandera activada EXIGE que esté puesta; no son alternativas, se acumulan. */
  soloFueraDeProgramacion?: boolean;
  soloRegistroTardio?: boolean;
  soloSinAsistencia?: boolean;
  /** Solo lo cargado a mano desde la bandeja (0374). */
  soloCargadosAMano?: boolean;
}

/**
 * Canjes para la bandeja del panel, por RANGO de fechas.
 *
 * Existe porque el tablero de Hoy solo mira el día en curso, y eso deja invisible justo lo que
 * hay que revisar: los registros de regularización llegan días después de la fecha que declaran.
 *
 * El filtro de dinámica se aplica en memoria: la dinámica vive en `canje_lines`, y filtrar por
 * una tabla embebida en PostgREST descartaría las OTRAS líneas del mismo registro — el cliente
 * se vería con menos canjes de los que tiene, que es exactamente el error del caso Efraín que
 * este módulo vino a corregir.
 */
export async function listCanjesBandeja(f: FiltrosBandeja): Promise<CanjeRow[]> {
  const filas = await leerCanjes((q) => {
    let x = q.gte('fecha', f.desde).lte('fecha', f.hasta);
    if (f.campaignId) x = x.eq('campaign_id', f.campaignId);
    if (f.pdvId) x = x.eq('pdv_id', f.pdvId);
    if (f.workerId) x = x.eq('worker_id', f.workerId);
    if (f.estado) x = x.eq('estado', f.estado);
    if (f.soloFueraDeProgramacion) x = x.eq('fuera_de_programacion', true);
    if (f.soloRegistroTardio) x = x.eq('registro_tardio', true);
    if (f.soloSinAsistencia) x = x.eq('sin_asistencia', true);
    if (f.soloCargadosAMano) x = x.eq('origen', 'bandeja');
    return x;
  });
  if (!f.dynamicId) return filas;
  return filas.filter((r) => r.lineas.some((l) => l.dynamicId === f.dynamicId));
}

/**
 * Las jornadas programadas del rango, con su persona aprobada y su marca de ingreso (0375). Sin campaña, las de todas
 * las campañas con canje que quien mira puede ver.
 *
 * Los canjes NO vienen acá: la vista «Por turno» los cruza con los que ya leyó `listCanjesBandeja`, para que el número
 * de cada turno y la lista de canjes salgan de la misma lectura.
 */
export async function jornadasParaCanjes(
  campaignId: string | null, desde: string, hasta: string,
): Promise<JornadaCanje[]> {
  const { data, error } = await supabase.rpc('jornadas_para_canjes', {
    p_campaign: campaignId, p_desde: desde, p_hasta: hasta,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as JornadaCanje[];
}

// `campaniasQueCoordino` vivía acá hasta la tanda del levantamiento (2026-08-19). Se mudó a
// `api/campaigns.ts` cuando la bandeja de levantamiento necesitó la MISMA lectura para su propia
// segunda puerta por fila: no es un dato de canjes sino de campañas, y dos copias de "de qué
// campañas soy el coordinador designado" se desincronizan sin que nada falle.

export interface CorreccionRow {
  id: string;
  campo: string;
  valorAnterior: string | null;
  valorNuevo: string | null;
  motivo: string;
  createdAt: string;
  actor: { firstNames: string; lastNames: string } | null;
}

/**
 * El rastro de correcciones de un registro, del más reciente al más antiguo.
 *
 * Es lo que hace legítimo corregir: en Sheets el arreglo borra lo que el campo reportó y nadie
 * distingue una corrección honesta de un dato inventado. Acá queda el valor anterior, quién y
 * por qué.
 */
export async function listCorreccionesDeCanje(recordId: string): Promise<CorreccionRow[]> {
  const { data, error } = await supabase
    .from('canje_corrections')
    .select(
      `id, campo, valor_anterior, valor_nuevo, motivo, created_at,
       actor:profiles!canje_corrections_actor_id_fkey(first_names, last_names)`,
    )
    .eq('record_id', recordId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  type Raw = {
    id: string; campo: string; valor_anterior: string | null; valor_nuevo: string | null;
    motivo: string; created_at: string;
    actor: { first_names: string; last_names: string } | null;
  };
  return ((data ?? []) as unknown as Raw[]).map((r) => ({
    id: r.id,
    campo: r.campo,
    valorAnterior: r.valor_anterior,
    valorNuevo: r.valor_nuevo,
    motivo: r.motivo,
    createdAt: r.created_at,
    actor: r.actor
      ? { firstNames: r.actor.first_names, lastNames: r.actor.last_names }
      : null,
  }));
}

/**
 * Canjes de un DÍA, de todas las campañas que la persona pueda ver. Para el tablero de Hoy.
 *
 * Es la lectura que sostiene la columna de dinámica: el tablero muestra una fila por jornada y
 * necesita saber cuántos canjes lleva cada una. Se agrupa después por PDV, que es lo que
 * identifica la fila.
 */
export function listCanjesDelDiaTodos(fecha: string): Promise<CanjeRow[]> {
  return leerCanjes((q) => q.eq('fecha', fecha).eq('estado', 'registrado'));
}

/**
 * Tiendas donde ESE día corre alguna dinámica vigente.
 *
 * El tablero la usa para distinguir "aquí no aplica" de "aplica y va en cero". Antes se deducía
 * de si había registros, que es la señal más barata pero **falsa en el caso que más importa**:
 * una tienda con dinámica que todavía no registró nada se veía igual que una sin dinámica — o
 * sea, el hueco quedaba escondido justo cuando había que mirarlo.
 */
export async function pdvsConDinamica(fecha: string): Promise<Set<string>> {
  const { data, error } = await supabase.rpc('pdvs_con_dinamica', { p_fecha: fecha });
  // Si falla, se devuelve vacío: el tablero mostrará guiones en vez de ceros. Prefiere callar
  // a inventar que una tienda tiene dinámica.
  if (error) return new Set();
  return new Set(((data ?? []) as { pdv_id: string }[]).map((r) => r.pdv_id));
}

/**
 * La clave del mapa de indicadores: **campaña + tienda**, nunca la tienda sola.
 *
 * Existe como función y no como plantilla escrita en cada sitio para que los dos lados —el que
 * arma el mapa y el que lo consulta— no puedan divergir. Cuando divergieron, el síntoma fue una
 * campaña mostrando la dinámica de otra, y no se vio hasta que el usuario abrió el detalle.
 */
export const claveDinamica = (campaignId: string, pdvId: string): string =>
  `${campaignId}|${pdvId}`;

export interface KpiDelDia {
  /**
   * La campaña dueña de la dinámica. Está acá desde la 0095 y NO es redundante con el PDV: dos
   * campañas comparten tienda el mismo día —Bimbo y Makro en Santa Anita— y solo una tiene
   * dinámica. Buscando solo por tienda, la otra hereda un indicador que no le corresponde.
   */
  campaignId: string;
  pdvId: string;
  /** Cómo se llama el indicador en ESA campaña: "Raspa y Gana", "Canjes", "Degustaciones". */
  label: string;
  metaDia: number | null;
}

/**
 * Qué indicador de dinámica aplica a cada tienda ese día, con su nombre y su meta (0035).
 *
 * El nombre importa: el usuario lo pidió porque **cada campaña tiene los suyos** y hay campañas
 * sin ninguna dinámica. Rotular "Canjes" una columna que en Makro son raspaditas y en otra
 * campaña serán degustaciones convierte el indicador en algo que nadie sabe leer.
 */
export async function kpiDinamicaDelDia(fecha: string): Promise<Map<string, KpiDelDia>> {
  const { data, error } = await supabase.rpc('kpi_dinamica_del_dia', { p_fecha: fecha });
  if (error) return new Map();
  const filas = (data ?? []) as {
    campaign_id: string; pdv_id: string; kpi_label: string; kpi_meta_dia: number | null;
  }[];
  return new Map(filas.map((f) => [
    claveDinamica(f.campaign_id, f.pdv_id),
    { campaignId: f.campaign_id, pdvId: f.pdv_id, label: f.kpi_label, metaDia: f.kpi_meta_dia },
  ]));
}

/**
 * Jornadas de UN trabajador en un periodo, con lo que registró en cada una.
 *
 * Cruza tres cosas que hoy viven separadas: sus posiciones aprobadas, si marcó ingreso, y sus
 * canjes. Es la base de la evaluación de desempeño, así que se lee de las tablas y no de un
 * resumen precalculado: cada número tiene que poder abrirse y comprobarse.
 */
export async function jornadasDeTrabajador(
  workerId: string, desde: string, hasta: string,
): Promise<{
  fecha: string; pdvId: string | null; pdvName: string | null; campaign: string;
  asistio: boolean; canjes: number;
}[]> {
  const [asignaciones, canjes] = await Promise.all([
    supabase
      .from('assignments')
      .select(`id, position:positions!inner(date, pdv_id, pdv:pdv(name),
               campaign:campaigns(client, brand)), attendance(kind)`)
      .eq('worker_id', workerId)
      .eq('status', 'aprobada')
      .gte('position.date', desde)
      .lte('position.date', hasta),
    leerCanjes((q) => q.eq('worker_id', workerId).gte('fecha', desde).lte('fecha', hasta)
      .eq('estado', 'registrado')),
  ]);
  if (asignaciones.error) throw new Error(asignaciones.error.message);

  type Raw = {
    position: {
      date: string; pdv_id: string | null;
      pdv: { name: string } | null;
      campaign: { client: string; brand: string } | null;
    };
    attendance: { kind: string }[] | null;
  };

  // Los canjes se cuentan por FECHA + TIENDA: es como se relacionan con la jornada sin depender
  // de que el registro traiga su `assignment_id` (puede venir nulo si se registró fuera de
  // programación).
  const porDiaTienda = new Map<string, number>();
  for (const c of canjes) {
    const k = `${c.fecha}|${c.pdv?.id ?? ''}`;
    porDiaTienda.set(k, (porDiaTienda.get(k) ?? 0) + c.totalCanjes);
  }

  return ((asignaciones.data ?? []) as unknown as Raw[])
    .map((a) => ({
      fecha: a.position.date,
      pdvId: a.position.pdv_id,
      pdvName: a.position.pdv?.name ?? null,
      campaign: a.position.campaign
        ? `${a.position.campaign.client} · ${a.position.campaign.brand}` : '—',
      asistio: (a.attendance ?? []).some((m) => m.kind === 'ingreso'),
      canjes: porDiaTienda.get(`${a.position.date}|${a.position.pdv_id ?? ''}`) ?? 0,
    }))
    .sort((x, y) => x.fecha.localeCompare(y.fecha));
}

export interface JornadaEquipo {
  workerId: string;
  workerNombre: string;
  workerDni: string;
  fecha: string;
  pdvId: string | null;
  asistio: boolean;
  canjes: number;
}

/**
 * Las jornadas de TODO el equipo de una campaña en un periodo (U9).
 *
 * El supervisor necesita mirar a su gente junta, no de a uno: *"que el supervisor vea el resumen
 * de todos los recursos de su campaña"*. Con la vista individual, comparar veinte personas exige
 * abrir veinte pantallas y anotar a mano.
 *
 * Se lee de las tablas y no de un resumen precalculado, por la misma razón que la vista
 * individual: son las cuentas con las que se evalúa a una persona y cada número tiene que poder
 * abrirse y comprobarse.
 *
 * **Pagina con `.range()` y compara contra `count: 'exact'`.** Un mes de MAKRO son 326 posiciones
 * y PostgREST corta en 1000 filas sin avisar: un supervisor con dos campañas grandes vería un
 * resumen incompleto sin ninguna señal, y eso es peor que no mostrarlo (§5).
 */
export async function jornadasDelEquipo(
  campaignId: string, desde: string, hasta: string,
): Promise<JornadaEquipo[]> {
  const PAGINA = 500;
  type Raw = {
    worker_id: string;
    worker: { dni: string; first_names: string; last_names: string } | null;
    position: { date: string; pdv_id: string | null };
    attendance: { kind: string }[] | null;
  };

  const filas: Raw[] = [];
  let desdeFila = 0;
  let total = Infinity;
  while (filas.length < total) {
    const { data, error, count } = await supabase
      .from('assignments')
      .select(
        `worker_id,
         worker:profiles!assignments_worker_id_fkey(dni, first_names, last_names),
         position:positions!inner(date, pdv_id),
         attendance(kind)`,
        { count: 'exact' },
      )
      .eq('status', 'aprobada')
      .eq('position.campaign_id', campaignId)
      .gte('position.date', desde)
      .lte('position.date', hasta)
      .range(desdeFila, desdeFila + PAGINA - 1);
    if (error) throw new Error(error.message);
    total = count ?? 0;
    const lote = (data ?? []) as unknown as Raw[];
    filas.push(...lote);
    if (lote.length === 0) break;
    desdeFila += PAGINA;
  }
  if (total !== Infinity && filas.length !== total) {
    throw new Error(
      `El resumen del equipo quedó incompleto (${filas.length} de ${total} jornadas). `
      + 'No se muestra a medias: vuelve a intentarlo.',
    );
  }
  // El supervisor de ruta no lee `profiles` (0218): sin esto, todo su equipo sale «Recurso no visible».
  await completarTrabajadores(filas);

  const canjes = await leerCanjes((q) =>
    q.eq('campaign_id', campaignId).gte('fecha', desde).lte('fecha', hasta)
      .eq('estado', 'registrado'));

  // Igual que en la vista individual: los canjes se atribuyen por PERSONA + FECHA + TIENDA, sin
  // depender de que el registro traiga su `assignment_id` (puede venir nulo si se registró fuera
  // de programación).
  const porPersonaDiaTienda = new Map<string, number>();
  for (const c of canjes) {
    const k = `${c.worker?.id ?? ''}|${c.fecha}|${c.pdv?.id ?? ''}`;
    porPersonaDiaTienda.set(k, (porPersonaDiaTienda.get(k) ?? 0) + c.totalCanjes);
  }

  return filas.map((a) => ({
    workerId: a.worker_id,
    workerNombre: a.worker ? `${a.worker.first_names} ${a.worker.last_names}` : 'Recurso no visible',
    // `||` y no `??`: el nombre completado para el supervisor trae el DNI vacío a propósito.
    workerDni: a.worker?.dni || '—',
    fecha: a.position.date,
    pdvId: a.position.pdv_id,
    asistio: (a.attendance ?? []).some((m) => m.kind === 'ingreso'),
    canjes: porPersonaDiaTienda.get(`${a.worker_id}|${a.position.date}|${a.position.pdv_id ?? ''}`) ?? 0,
  }));
}

export function listCanjesDeCampania(
  campaignId: string, dateFrom?: string, dateTo?: string,
): Promise<CanjeRow[]> {
  return leerCanjes((q) => {
    let con = q.eq('campaign_id', campaignId);
    if (dateFrom) con = con.gte('fecha', dateFrom);
    if (dateTo) con = con.lte('fecha', dateTo);
    return con;
  });
}

/**
 * Canjes propios de un día, para la pantalla móvil.
 *
 * Se acota por `worker_id` además de por RLS: la RLS deja al coordinador ver toda su campaña,
 * así que sin este filtro su contador del día mostraría los canjes de todo el mundo.
 */
/**
 * Mis canjes DESDE una fecha, para el historial. Igual que `listMisCanjesDelDia` pero por rango:
 * el historial no muestra un día, muestra los últimos.
 */
export async function listMisCanjesDesde(dateFrom: string): Promise<CanjeRow[]> {
  const { data: sesion } = await supabase.auth.getUser();
  const uid = sesion?.user?.id;
  if (!uid) return [];
  return leerCanjes((q) => q.eq('worker_id', uid).gte('fecha', dateFrom));
}

export async function listMisCanjesDelDia(fecha: string): Promise<CanjeRow[]> {
  const { data: sesion } = await supabase.auth.getUser();
  const uid = sesion?.user?.id;
  if (!uid) return [];
  return leerCanjes((q) => q.eq('worker_id', uid).eq('fecha', fecha));
}

// ---------------------------------------------------------------------------
// Corregir y anular (jefe de operaciones)
// ---------------------------------------------------------------------------

/**
 * Corrige campos del registro dejando rastro. El servidor exige motivo y solo acepta los
 * campos de su lista blanca; los valores van como texto y él castea al tipo de la columna.
 *
 * Dos cosas que dependen de la 0057 y que hay que respetar desde acá:
 *
 * - **Cadena vacía = borrar el dato** (queda NULL). Antes de la 0057 mandar `''` sobre una columna
 *   `date`, `numeric` o `uuid` reventaba con el error crudo de Postgres, así que el único cambio
 *   posible era de un valor a otro. Ahora se puede vaciar — salvo `fecha` y `pdv_id`, que son
 *   obligatorias y el servidor rechaza con un mensaje que dice cuál.
 * - **Las rutas de foto se validan por prefijo**: solo `form-photos/…`, nada más. No es un
 *   capricho: el bucket `photos` guarda también las fotos de DNI en `documents/`, y el Excel de
 *   canjes firma lo que encuentre en esas columnas con un enlace que abre sin sesión y dura un
 *   año. La 0057 llegó a aceptar además un `https://` —por simetría con la 0045, que es de
 *   asistencia y no de canjes— y se quitó: ningún canje nace con la foto en una URL, y esa rama
 *   dejaba poner «Ver boleta» apuntando a un sitio ajeno dentro del archivo que se le manda al
 *   cliente.
 */
export async function corregirCanje(
  recordId: string, cambios: Record<string, string>, motivo: string,
): Promise<void> {
  const { error } = await supabase.rpc('corregir_canje', {
    p_record: recordId,
    p_cambios: cambios,
    p_motivo: motivo,
  });
  if (error) throw new Error(error.message);
}

/** Marca el canje como anulado (no lo borra) y deja el motivo en el rastro. */
export async function anularCanje(recordId: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('anular_canje', {
    p_record: recordId,
    p_motivo: motivo,
  });
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Registro del enviador de la cola offline
//
// Se hace al importarse este módulo, igual que `api/attendance.ts` con la cola de asistencia:
// así la cola no depende de un módulo que se carga después, y cualquiera que importe la API
// deja la cola lista para vaciarse.
// ---------------------------------------------------------------------------

setCanjeUploader({
  uploadCanjePhoto,
  registrarCanje,
  isPermanentError: esErrorPermanente,
});
/**
 * Borra el registro de verdad. Exige motivo, que queda en la constancia interna de `audit_log`.
 *
 * Es borrado REAL y no un estado «anulado», por decision del usuario: *«preferiria borrado real
 * porque no deberia haber rastro, es error total, pero igual que quede documentado que se borro a
 * la interna»*. La constancia la deja un disparador `before delete` con la fila entera (0101), asi
 * que no depende de que la app se acuerde de escribirla.
 */
export async function borrarCanje(id: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('borrar_canje', { p_id: id, p_motivo: motivo });
  if (error) throw new Error(error.message);
}
